/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useMemo } from '@web-stories-wp/react';
import {
  calcRotatedObjectPositionAndSize,
  dataPixels,
  editorToDataY,
  getBoundRect,
  PAGE_WIDTH,
  PAGE_HEIGHT,
} from '@web-stories-wp/units';
/**
 * Internal dependencies
 */
import { useStory } from '../../../../app';
import { isTextBoxAttachment } from '../../../../utils/textBoxAttachment';

const PAGE_RECT = {
  startX: 0,
  startY: 0,
  endX: PAGE_WIDTH,
  endY: PAGE_HEIGHT,
  width: PAGE_WIDTH,
  height: PAGE_HEIGHT,
};

const ALIGNMENT = {
  LEFT: 'left',
  RIGHT: 'right',
  TOP: 'top',
  BOTTOM: 'bottom',
};

type DistributionItem = {
  id: string;
  elementIds: string[];
  frameX: number;
  frameY: number;
  frameWidth: number;
  frameHeight: number;
};

type DistributionAxis = 'x' | 'y';

function useAlignment() {
  const {
    selectedElements,
    selectedElementIds,
    currentPage,
    updateElementsById,
  } = useStory(({ state, actions }) => ({
    selectedElements: state.selectedElements,
    selectedElementIds: state.selectedElementIds,
    currentPage: state.currentPage,
    updateElementsById: actions.updateElementsById,
  }));

  const isPositionLocked = selectedElements.some(({ textBoxAttachment }) =>
    isTextBoxAttachment(textBoxAttachment)
  );

  const updateElements = (callback) => {
    if (isPositionLocked) {
      return;
    }
    updateElementsById({
      elementIds: selectedElementIds,
      properties: callback,
      attachmentBehavior: 'rebase',
    });
  };

  const selectedElementsWithFrame = useMemo(
    () =>
      selectedElements.map((item) => {
        const { id, groupId, x, y, width, height, rotationAngle } = item;
        let frameX = x;
        let frameY = y;
        let frameWidth = width;
        let frameHeight = height;
        if (rotationAngle) {
          const elementFrame = calcRotatedObjectPositionAndSize(
            rotationAngle,
            x,
            y,
            width,
            height
          );
          frameX = elementFrame.x;
          frameY = elementFrame.y;
          frameWidth = elementFrame.width;
          frameHeight = elementFrame.height;
        }
        return {
          id,
          groupId,
          x,
          y,
          width,
          height,
          frameX,
          frameY,
          frameWidth,
          frameHeight,
        };
      }),
    [selectedElements]
  );

  const selectedElementsById = useMemo(
    () => new Map(selectedElements.map((element) => [element.id, element])),
    [selectedElements]
  );

  const selectedElementIdsSet = useMemo(
    () => new Set(selectedElementIds),
    [selectedElementIds]
  );

  const groupElementsById = useMemo(() => {
    const groups = new Map();
    if (!currentPage) {
      return groups;
    }
    currentPage.elements.forEach((element) => {
      if (!element.groupId) {
        return;
      }
      if (!groups.has(element.groupId)) {
        groups.set(element.groupId, []);
      }
      groups.get(element.groupId).push(element);
    });
    return groups;
  }, [currentPage]);

  const fullySelectedGroupIds = useMemo(() => {
    const groupIds = new Set(
      selectedElements.map((element) => element.groupId).filter(Boolean)
    );
    return new Set(
      Array.from(groupIds).filter((groupId) => {
        const groupElements = groupElementsById.get(groupId) || [];
        return (
          groupElements.length > 0 &&
          groupElements.every((element) =>
            selectedElementIdsSet.has(element.id)
          )
        );
      })
    );
  }, [groupElementsById, selectedElements, selectedElementIdsSet]);

  const distributionItems = useMemo(() => {
    const items: DistributionItem[] = [];

    selectedElementsWithFrame.forEach((element) => {
      if (!element.groupId || !fullySelectedGroupIds.has(element.groupId)) {
        items.push({
          id: element.id,
          elementIds: [element.id],
          frameX: element.frameX,
          frameY: element.frameY,
          frameWidth: element.frameWidth,
          frameHeight: element.frameHeight,
        });
      }
    });

    fullySelectedGroupIds.forEach((groupId) => {
      const groupElements = groupElementsById.get(groupId) || [];
      if (groupElements.length === 0) {
        return;
      }
      const groupRect = getBoundRect(groupElements);
      items.push({
        id: groupId,
        elementIds: groupElements.map((element) => element.id),
        frameX: groupRect.startX,
        frameY: groupRect.startY,
        frameWidth: groupRect.width,
        frameHeight: groupRect.height,
      });
    });

    return items;
  }, [fullySelectedGroupIds, groupElementsById, selectedElementsWithFrame]);

  const distributionBoundRect = useMemo(() => {
    if (distributionItems.length === 0) {
      return null;
    }
    return getBoundRect(
      distributionItems.map((item) => ({
        x: item.frameX,
        y: item.frameY,
        width: item.frameWidth,
        height: item.frameHeight,
        rotationAngle: 0,
      }))
    );
  }, [distributionItems]);

  const isDistributionEnabled =
    !isPositionLocked && distributionItems.length > 2;

  // Set boundRect with pageSize when there is only one element selected or only group is selected
  const selectedGroupId = selectedElements[0]?.groupId;
  const isOnlyGroupSelected =
    selectedGroupId &&
    selectedElements.every(
      (el) => el.groupId && el.groupId === selectedGroupId
    );
  const boundRect =
    selectedElements.length === 1 || isOnlyGroupSelected
      ? PAGE_RECT
      : getBoundRect(selectedElements);

  const handleAlign = (direction) => {
    updateElements((properties) => {
      const { id, groupId } = properties;
      const groupRect = groupId
        ? getBoundRect(selectedElements.filter((el) => el.groupId === groupId))
        : null;

      const element = selectedElementsWithFrame.find((item) => item.id === id);
      const {
        width = 0,
        height = 0,
        frameWidth = 0,
        frameHeight = 0,
      } = element || {};
      const offset =
        direction === ALIGNMENT.LEFT || direction === ALIGNMENT.RIGHT
          ? (frameWidth - width) / 2
          : (frameHeight - height) / 2;
      const offsetInGroup = groupId
        ? direction === ALIGNMENT.LEFT || direction === ALIGNMENT.RIGHT
          ? element.frameX - groupRect.startX
          : element.frameY - groupRect.startY
        : 0;

      const calcWidth = groupId ? groupRect.width : width;
      const calcHeight = groupId ? groupRect.height : height;

      if (direction === ALIGNMENT.LEFT || direction === ALIGNMENT.RIGHT) {
        return {
          x:
            direction === ALIGNMENT.LEFT
              ? boundRect.startX + offset + offsetInGroup
              : boundRect.endX - calcWidth - offset + offsetInGroup,
        };
      }
      return {
        y:
          direction === ALIGNMENT.TOP
            ? boundRect.startY + offset + offsetInGroup
            : boundRect.endY - calcHeight - offset + offsetInGroup,
      };
    });
  };

  const handleAlignCenter = () => {
    const centerX = (boundRect.endX + boundRect.startX) / 2;
    updateElements((properties) => {
      const { id, width, groupId } = properties;
      const groupRect = groupId
        ? getBoundRect(selectedElements.filter((el) => el.groupId === groupId))
        : null;
      const calcWidth = groupId ? groupRect.width : width;
      const element = selectedElementsWithFrame.find((item) => item.id === id);
      const offsetInGroup = groupId ? element.frameX - groupRect.startX : 0;
      return {
        x: centerX - calcWidth / 2 + offsetInGroup,
      };
    });
  };

  const handleAlignMiddle = () => {
    const centerY = (boundRect.endY + boundRect.startY) / 2;
    updateElements((properties) => {
      const { id, height, groupId } = properties;
      const groupRect = groupId
        ? getBoundRect(selectedElements.filter((el) => el.groupId === groupId))
        : null;
      const calcHeight = groupId ? groupRect.height : height;
      const element = selectedElementsWithFrame.find((item) => item.id === id);
      const offsetInGroup = groupId ? element.frameY - groupRect.startY : 0;
      return {
        y: centerY - calcHeight / 2 + offsetInGroup,
      };
    });
  };

  const getDistributionUpdates = (axis: DistributionAxis) => {
    if (!distributionBoundRect || distributionItems.length < 3) {
      return null;
    }

    const frameStartKey = axis === 'x' ? 'frameX' : 'frameY';
    const frameSizeKey = axis === 'x' ? 'frameWidth' : 'frameHeight';
    const boundStart =
      axis === 'x'
        ? distributionBoundRect.startX
        : distributionBoundRect.startY;
    const boundSize =
      axis === 'x' ? distributionBoundRect.width : distributionBoundRect.height;

    const sortedItems = [...distributionItems].sort(
      (a, b) =>
        a[frameStartKey] +
        a[frameSizeKey] / 2 -
        (b[frameStartKey] + b[frameSizeKey] / 2)
    );

    const itemSizeSum = sortedItems.reduce(
      (sum, item) => sum + item[frameSizeKey],
      0
    );
    const gap = dataPixels(
      (boundSize - itemSizeSum) / (sortedItems.length - 1)
    );

    const updates = {};
    let offset = boundStart;

    sortedItems.forEach((item) => {
      const delta = offset - item[frameStartKey];
      item.elementIds.forEach((elementId) => {
        const element = selectedElementsById.get(elementId);
        if (!element) {
          return;
        }
        updates[elementId] = {
          [axis]: element[axis] + delta,
        };
      });
      offset += item[frameSizeKey] + gap;
    });

    return updates;
  };

  const handleHorizontalDistribution = () => {
    if (isPositionLocked) {
      return;
    }
    const updates = getDistributionUpdates('x');
    if (!updates) {
      return;
    }
    updateElementsById({
      elementIds: Object.keys(updates),
      properties: ({ id }) => updates[id],
      attachmentBehavior: 'rebase',
    });
  };

  const handleVerticalDistribution = () => {
    if (isPositionLocked) {
      return;
    }
    const updates = getDistributionUpdates('y');
    if (!updates) {
      return;
    }
    updateElementsById({
      elementIds: Object.keys(updates),
      properties: ({ id }) => updates[id],
      attachmentBehavior: 'rebase',
    });
  };

  return {
    isPositionLocked,
    isDistributionEnabled,
    handleAlignLeft: () => handleAlign(ALIGNMENT.LEFT),
    handleAlignCenter,
    handleAlignRight: () => handleAlign(ALIGNMENT.RIGHT),
    handleAlignTop: () => handleAlign(ALIGNMENT.TOP),
    handleAlignMiddle,
    handleAlignBottom: () => handleAlign(ALIGNMENT.BOTTOM),
    handleHorizontalDistribution,
    handleVerticalDistribution,
  };
}

export default useAlignment;
